import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import '../../../config/chart';
import {
  useReportsData,
  useAdsCreatedTrends,
  useCampaignsCreatedTrends,
} from '@/hooks/api/useAdvertisersData';
import type { ReportsDataItem, TrendsDataPoint } from '@/types/advertisers.types';

interface AdvertiserPerformanceProps {
  selectedAdvertiser?: string;
  dateRange?: { from: string; to: string };
}

interface ChartData {
  date: string;
  roas: number;
  revenue: number;
  spend: number;
  pl: number;
  ctr: number;
  newAds: number;
  newCampaigns: number;
}

const AdvertiserPerformance = ({
  selectedAdvertiser = '',
  dateRange,
}: AdvertiserPerformanceProps) => {
  const theme = useTheme();

  // Metric selections for 3 trends
  const [metric1, setMetric1] = useState('roas');
  const [metric2, setMetric2] = useState('newAds');
  const [metric3, setMetric3] = useState('newCampaigns');

  // Fetch data using React Query hooks
  const {
    data: reportsResult,
    isLoading: isLoadingReports,
  } = useReportsData(
    {
      fromDate: dateRange?.from || '',
      toDate: dateRange?.to || '',
    },
    {
      enabled: Boolean(dateRange?.from && dateRange?.to),
    }
  );

  const {
    data: adsCreatedResult,
    isLoading: isLoadingAds,
  } = useAdsCreatedTrends(
    {
      fromDate: dateRange?.from,
      toDate: dateRange?.to,
      advertiser: selectedAdvertiser || undefined,
    },
    {
      enabled: Boolean(dateRange?.from && dateRange?.to),
    }
  );

  const {
    data: campaignsCreatedResult,
    isLoading: isLoadingCampaigns,
  } = useCampaignsCreatedTrends(
    {
      fromDate: dateRange?.from,
      toDate: dateRange?.to,
      advertiser: selectedAdvertiser || undefined,
    },
    {
      enabled: Boolean(dateRange?.from && dateRange?.to),
    }
  );

  const loading = isLoadingReports || isLoadingAds || isLoadingCampaigns;

  const metricOptions = [
    { value: 'roas', label: 'ROAS (%)' },
    { value: 'revenue', label: 'Revenue ($k)' },
    { value: 'spend', label: 'Spend ($k)' },
    { value: 'pl', label: 'P&L ($k)' },
    { value: 'ctr', label: 'CTR (%)' },
    { value: 'newAds', label: 'New Ads Created' },
    { value: 'newCampaigns', label: 'New Campaigns Created' },
  ];

  const getMetricLabel = (metric: string) => {
    const option = metricOptions.find(m => m.value === metric);
    return option ? option.label : metric;
  };

  // Process chart data using React Query results
  const chartData = useMemo(() => {
    const data = reportsResult?.data || [];
    const adsCreatedData = adsCreatedResult?.data || [];
    const campaignsCreatedData = campaignsCreatedResult?.data || [];

    // Create maps for quick lookup of trends data by date
    const adsCreatedByDate: { [key: string]: number } = {};
    adsCreatedData.forEach((item: TrendsDataPoint) => {
      adsCreatedByDate[item.date] = item.count;
    });

    const campaignsCreatedByDate: { [key: string]: number } = {};
    campaignsCreatedData.forEach((item: TrendsDataPoint) => {
      campaignsCreatedByDate[item.date] = item.count;
    });

    // Filter by selected advertiser
    const filteredData = !selectedAdvertiser
      ? data
      : data.filter((item: ReportsDataItem) => item.advertiserName === selectedAdvertiser);

    // Group by date
    const dateMetrics: {
      [key: string]: {
        date: string;
        revenue: number;
        spend: number;
        clicks: number;
        impressions: number;
        searches: number;
        newAds: number;
        newCampaigns: number;
      };
    } = {};

    filteredData.forEach((item: ReportsDataItem) => {
      // Date is already in YYYY-MM-DD format from backend
      const dateStr = item.date;
      if (!dateStr) return;

      if (!dateMetrics[dateStr]) {
        dateMetrics[dateStr] = {
          date: dateStr,
          revenue: 0,
          spend: 0,
          clicks: 0,
          impressions: 0,
          searches: 0,
          newAds: adsCreatedByDate[dateStr] || 0,
          newCampaigns: campaignsCreatedByDate[dateStr] || 0,
        };
      }

      dateMetrics[dateStr].revenue += parseFloat(String(item.reportedNetRevenue || 0)) || 0;
      dateMetrics[dateStr].spend += parseFloat(String(item.fbSpend || 0)) || 0;
      dateMetrics[dateStr].clicks += parseInt(String(item.reportedClicks || 0)) || 0;
      dateMetrics[dateStr].impressions += parseInt(String(item.fbImpressions || 0)) || 0;
      dateMetrics[dateStr].searches += parseInt(String(item.actualSearches || 0)) || 0;
    });

    // Calculate metrics
    return Object.keys(dateMetrics)
      .sort()
      .map(date => {
        const data = dateMetrics[date];
        const roas = data.spend > 0 ? (data.revenue / data.spend) * 100 : 0;
        const ctr = data.searches > 0 ? (data.clicks / data.searches) * 100 : 0;
        const revenueK = data.revenue / 1000;
        const spendK = data.spend / 1000;
        const pl = (data.revenue - data.spend) / 1000;

        // Parse YYYY-MM-DD format directly without timezone conversion
        const [year, month, day] = date.split('-');
        const formattedDate = `${new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;

        return {
          date: formattedDate,
          roas: Math.round(roas * 10) / 10,
          revenue: Math.round(revenueK * 10) / 10,
          spend: Math.round(spendK * 10) / 10,
          pl: Math.round(pl * 10) / 10,
          ctr: Math.round(ctr * 10) / 10,
          newAds: data.newAds,
          newCampaigns: data.newCampaigns,
        };
      });
  }, [reportsResult, adsCreatedResult, campaignsCreatedResult, selectedAdvertiser]);

  const getLineColor = (index: number) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ];
    return colors[index] || theme.palette.primary.main;
  };

  const chartJsData = {
    labels: chartData.map(item => item.date),
    datasets: [
      {
        label: getMetricLabel(metric1),
        data: chartData.map(item => item[metric1 as keyof ChartData] as number),
        borderColor: getLineColor(0),
        backgroundColor: `${getLineColor(0)}20`,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: getLineColor(0),
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        fill: false,
        tension: 0.4,
      },
      {
        label: getMetricLabel(metric2),
        data: chartData.map(item => item[metric2 as keyof ChartData] as number),
        borderColor: getLineColor(1),
        backgroundColor: `${getLineColor(1)}20`,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: getLineColor(1),
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        fill: false,
        tension: 0.4,
      },
      {
        label: getMetricLabel(metric3),
        data: chartData.map(item => item[metric3 as keyof ChartData] as number),
        borderColor: getLineColor(2),
        backgroundColor: `${getLineColor(2)}20`,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: getLineColor(2),
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        fill: false,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    layout: {
      padding: {
        left: -10,
        right: 0,
        top: 0,
        bottom: -10,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          padding: 15,
          font: {
            size: 12,
            family: "'Roboto', sans-serif",
          },
          color: theme.palette.text.secondary,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        usePointStyle: true,
        intersect: false,
        mode: 'index' as const,
        position: 'nearest' as const,
        animation: {
          duration: 200,
        },
        titleFont: {
          family: "'Roboto', sans-serif",
          size: 14,
          weight: 'bold' as const,
        },
        bodyFont: {
          family: "'Roboto', sans-serif",
          size: 12,
          weight: 'normal' as const,
        },
        padding: {
          top: 12,
          right: 16,
          bottom: 12,
          left: 16,
        },
        caretSize: 6,
        caretPadding: 6,
        multiKeyBackground: 'rgba(255, 255, 255, 0.1)',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: theme.palette.divider,
          borderDash: [3, 3],
          drawBorder: false,
          lineWidth: 1,
        },
        ticks: {
          font: {
            size: 10,
            family: "'Roboto', sans-serif",
          },
          color: theme.palette.text.secondary,
          padding: 10,
        },
        border: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Roboto', sans-serif",
          },
          color: theme.palette.text.secondary,
          padding: 10,
        },
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <Card
      sx={{
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
        border: 'none',
      }}
    >
      <CardContent sx={{ padding: '25px !important' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Advertiser Performance Trends
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Metric Selectors - Smaller and right-aligned */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Trend 1</InputLabel>
              <Select
                value={metric1}
                label="Trend 1"
                onChange={(e) => setMetric1(e.target.value)}
              >
                {metricOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Trend 2</InputLabel>
              <Select
                value={metric2}
                label="Trend 2"
                onChange={(e) => setMetric2(e.target.value)}
              >
                {metricOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Trend 3</InputLabel>
              <Select
                value={metric3}
                label="Trend 3"
                onChange={(e) => setMetric3(e.target.value)}
              >
                {metricOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ width: '100%', height: 400, mt: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">Loading chart data...</Typography>
            </Box>
          ) : chartData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">No data available</Typography>
            </Box>
          ) : (
            <Line data={chartJsData} options={chartOptions} height={400} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdvertiserPerformance;
